/**
 * Choosing what to add, and where it should land.
 *
 * A file picker on its own was not enough for the job people actually do. A
 * scanned archive is a *folder* — often a folder of folders — and picking two
 * thousand files out of a dialog by hand is not a thing anybody does twice. So
 * the folder picker is the first option and the file picker the second.
 *
 * The album choice is here rather than after the fact for the same reason: the
 * moment you know which album a batch belongs to is the moment you pick it,
 * and finding four hundred photographs again afterwards to file them is work
 * nobody should have to repeat.
 */

import { h } from "./dom";
import { api } from "./api";
import { store } from "./state";
import type { AlbumSummary } from "./types";

export interface Chosen {
  files: File[];
  /** Existing album id, a name to create, or nothing. */
  albumId: string | null;
  newAlbumName: string | null;
}

/** Remembered between batches: importing a folder at a time into one album. */
let lastAlbumId: string | null = null;

const NONE = "__none__";
const NEW = "__new__";

/**
 * Open the picker.
 *
 * Resolves with the files and where they go, or null if dismissed. Files are
 * gathered *before* the dialog closes so a slow directory read cannot leave
 * somebody looking at nothing.
 */
export async function choosePhotos(preselected?: File[]): Promise<Chosen | null> {
  let albums: AlbumSummary[] = [];
  try {
    albums = await api.albums(store.libraryId);
  } catch {
    // An archive with no albums yet, or a failed read: the choice degrades to
    // "no album", which is a fine default and not worth an error for.
  }

  const dialog = h("dialog", { class: "modal" });
  return new Promise<Chosen | null>((resolve) => {
    let files: File[] = preselected ?? [];
    let settled = false;

    const finish = (value: Chosen | null): void => {
      if (settled) return;
      settled = true;
      dialog.close();
      dialog.remove();
      resolve(value);
    };

    const count = h("p", { class: "modal-say" });
    const showCount = (): void => {
      count.textContent = files.length
        ? `${files.length.toLocaleString()} file${files.length === 1 ? "" : "s"} chosen`
        : "Nothing chosen yet.";
      confirm.disabled = files.length === 0;
    };

    /**
     * One hidden input per use.
     *
     * `webkitdirectory` cannot be toggled on a live input in every browser —
     * some cache the mode from first use — so a fresh element is made each
     * time rather than one being reconfigured.
     */
    const pick = (directory: boolean): void => {
      const input = h("input", {
        type: "file",
        multiple: "",
        style: "display:none",
      }) as HTMLInputElement;
      if (directory) {
        input.setAttribute("webkitdirectory", "");
        input.setAttribute("directory", "");
      } else {
        input.setAttribute("accept", "image/*,video/*");
      }
      input.addEventListener("change", () => {
        files = [...(input.files ?? [])];
        showCount();
        input.remove();
      });
      document.body.appendChild(input);
      input.click();
    };

    const select = h("select", { class: "modal-select" }) as HTMLSelectElement;
    select.appendChild(h("option", { value: NONE, text: "No album — just add them" }));
    for (const a of albums) {
      select.appendChild(
        h("option", {
          value: a.id,
          selected: a.id === lastAlbumId,
          text: `${a.name} · ${a.count.toLocaleString()}`,
        }),
      );
    }
    if (store.can("album.create")) {
      select.appendChild(h("option", { value: NEW, text: "New album…" }));
    }

    const newName = h("input", {
      type: "text",
      placeholder: "Name for the new album",
      class: "modal-newalbum",
      hidden: "",
    }) as HTMLInputElement;
    select.addEventListener("change", () => {
      newName.hidden = select.value !== NEW;
      if (!newName.hidden) newName.focus();
    });

    const confirm = h("button", {
      class: "btn primary",
      onclick: () => {
        const choice = select.value;
        if (choice === NEW && !newName.value.trim()) {
          newName.classList.add("bad");
          newName.focus();
          return;
        }
        if (choice !== NONE && choice !== NEW) lastAlbumId = choice;
        finish({
          files,
          albumId: choice === NONE || choice === NEW ? null : choice,
          newAlbumName: choice === NEW ? newName.value.trim() : null,
        });
      },
    }, "Add them") as HTMLButtonElement;

    dialog.appendChild(
      h(
        "div",
        { class: "modal-body" },
        h("h2", { class: "modal-title" }, "Add photographs"),
        h(
          "p",
          { class: "modal-say" },
          "A whole folder is usually what you want — sub-folders are walked " +
            "through, and anything already in the archive is skipped without " +
            "being uploaded again.",
        ),
        h(
          "div",
          { class: "modal-pickers" },
          h("button", { class: "btn", onclick: () => pick(true) }, "Choose a folder…"),
          h("button", { class: "btn ghost", onclick: () => pick(false) }, "Choose files…"),
        ),
        count,
        h(
          "label",
          { class: "modal-field" },
          h("span", { class: "modal-label" }, "Put them in"),
          select,
          newName,
          h("span", { class: "modal-hint" },
            "An album is a set you choose. Nothing is copied, and a " +
            "photograph can be in several."),
        ),
        h(
          "div",
          { class: "modal-actions" },
          h("button", { class: "btn ghost", onclick: () => finish(null) }, "Cancel"),
          confirm,
        ),
      ),
    );

    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) finish(null);
    });
    dialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      finish(null);
    });

    document.body.appendChild(dialog);
    dialog.showModal();
    showCount();
    if (!files.length) pickHint(dialog);
  });
}

/** Nudge focus onto the folder button, which is what most people want. */
function pickHint(dialog: HTMLElement): void {
  dialog.querySelector<HTMLButtonElement>(".modal-pickers .btn")?.focus();
}

/**
 * File the freshly added photographs, creating the album if that is what was
 * asked for.
 *
 * Returns what to tell the user, because "added 400" and "added 400 to
 * Christmases" are different sentences and the caller should not have to
 * reconstruct which happened.
 */
export async function fileIntoAlbum(
  chosen: Chosen,
  assetIds: string[],
): Promise<string | null> {
  if (!assetIds.length) return null;
  let albumId = chosen.albumId;
  let name: string | null = null;

  if (chosen.newAlbumName) {
    try {
      await api.createAlbum(store.libraryId, chosen.newAlbumName);
      const albums = await api.albums(store.libraryId);
      const made = albums.find((a) => a.name === chosen.newAlbumName);
      albumId = made?.id ?? null;
      name = chosen.newAlbumName;
      if (albumId) lastAlbumId = albumId;
    } catch {
      // The photographs are in; only the filing failed, and saying so is
      // better than implying the upload did.
      return "could not make the album — the photographs are in the archive";
    }
  }
  if (!albumId) return null;

  if (!name) {
    try {
      name = (await api.albums(store.libraryId)).find((a) => a.id === albumId)?.name ?? null;
    } catch {
      /* the name is decoration here */
    }
  }
  try {
    // Chunked: a folder import can be thousands, and one request carrying all
    // of them is a request that times out.
    for (let i = 0; i < assetIds.length; i += 200) {
      await api.addToAlbum(store.libraryId, albumId, assetIds.slice(i, i + 200));
    }
    return name ? `filed into ${name}` : null;
  } catch {
    return "could not file them into the album";
  }
}
